import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type { ServerToClientEvents } from '@adflow/shared/util-websockets';

import { useWebsocketListener } from './use-websocket-listener';

interface UploadProgress {
	complete: number;
	total: number;
}

type UploadProgressEventName =
	| (typeof ServerToClientEvents)['FacebookAdsUploadProgress']
	| (typeof ServerToClientEvents)['GoogleAdsUploadProgress'];

interface UseTemplateUploadProgressOptions {
	templateId: number;
	queryHook: (
		templateId: number,
		enabled: boolean
	) => {
		data: UploadProgress | null | undefined;
	};
	queryKey: (templateId: number) => readonly unknown[];
	websocketEvent: UploadProgressEventName;
	enabled?: boolean;
}

export function useTemplateUploadProgress({
	templateId,
	queryHook,
	queryKey,
	websocketEvent,
	enabled = true,
}: UseTemplateUploadProgressOptions) {
	const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
		null
	);
	const [queryEnabled, setQueryEnabled] = useState(
		enabled && templateId !== -1
	);
	const queryClient = useQueryClient();
	const hasInitializedRef = useRef(false);

	const { data: initialProgress } = queryHook(templateId, queryEnabled);

	useEffect(() => {
		if (
			!hasInitializedRef.current &&
			initialProgress &&
			uploadProgress === null
		) {
			setUploadProgress(initialProgress);
			hasInitializedRef.current = true;
			// Remove the query cache and disable the query
			queryClient.removeQueries({
				queryKey: queryKey(templateId),
			});
			setQueryEnabled(false);
		}
	}, [initialProgress, uploadProgress, queryClient, queryKey, templateId]);

	useWebsocketListener(
		websocketEvent,
		({
			templateId: eventTemplateId,
			complete,
			total,
		}: {
			templateId: number;
			complete: number;
			total: number;
		}) => {
			if (eventTemplateId === templateId) {
				if (complete === total) {
					setTimeout(() => setUploadProgress(null), 500);
				}
				setUploadProgress({ complete, total });
			}
		}
	);

	return {
		uploadProgress,
		setUploadProgress,
	};
}
